import type { RoadmapCompanyConfig } from '@/types/roadmap.types';
import { GENERATED_PROGRAMS } from './programs.generated';
// switch to PROPOSAL_PROGRAMS when ready to submit proposals as well as on line 170

export const tetrasoftConfig: RoadmapCompanyConfig = {
  id: 'tetrasoft',

  clientBrand: {
    logo: '/brands/tetrasoft/logo.webp',
    companyName: 'Tetrasoft',
    colors: {
      primary: '#7B2D8E',
      secondary: '#1c3459',
    },
  },

  company: {
    name: 'Tetrasoft',
    contact_name: 'Leadership Team',
    contact_title: 'CEO',
    logo: undefined,
    founded_year: 2015,
    headquarters: { city: 'Chesterfield', state: 'MO', country: 'US' },
    additional_locations: [
      { city: 'Rzeszów', country: 'Poland', status: 'active' },
      { city: 'Zamość', country: 'Poland', status: 'planned' },
    ],
    employee_count: 35,
    annual_revenue_range: '$2M – $10M',
    entity_type: 'for-profit',
    industry: 'Defense / Advanced RF & Antenna Systems',
    industry_tags: [
      'multi-beam antenna systems',
      'RF sensing',
      'ISAC',
      'spectrum awareness',
      'electronic warfare',
      'satellite communications',
      'defense communications',
      'AI signal processing',
      'dual-use technology',
    ],
    technology_description:
      'Advanced multi-beam antenna systems (MBAS) with integrated sensing and communication (ISAC), electromagnetic environment sensing, AI-enabled signal optimization, and space-air-ground integrated networks (SAGIN). Capabilities span defense communications (TACOM, secure networks), satellite communications (LEO/GEO), electronic warfare, and cognitive radio.',
    technology_keywords: [
      'MBAS',
      'ISAC',
      'RF sensing',
      'spectrum awareness',
      'electronic warfare',
      'SAGIN',
      'cognitive radio',
      'satellite communications',
      'LEO',
      'GEO',
      'beamforming',
      'phased array',
    ],
    trl: 5,
    development_status: 'pre-commercial',
    certifications: [],
    ownership_demographics: [],
  },

  intro_statement:
    'This roadmap maps funding opportunities for Tetrasoft\u2019s RF and multi-beam antenna stack across four investment layers to build a realistic path to external funding over the next 12\u201318 months.',

  investment_layers: [
    {
      id: 'rd',
      label: 'Core R&D – RF Systems & AI',
      description:
        'Multi-beam antenna architecture, ISAC development, sensing algorithms, AI-enabled signal processing, and cognitive radio.',
      priority: 'high',
      details: [
        'RF system architecture and signal processing',
        'Integrated Sensing and Communication (ISAC) development',
        'AI/ML-based signal optimization and spectrum awareness',
        'Electromagnetic environment sensing algorithms',
      ],
    },
    {
      id: 'testing',
      label: 'Testing & Validation Infrastructure',
      description:
        'Antenna testing environments, RF validation labs, system integration testbeds, and communications network simulation.',
      priority: 'high',
      details: [
        'Antenna testing environments',
        'RF validation laboratories',
        'System integration testbeds',
        'Communications network simulation environments',
      ],
    },
    {
      id: 'prototyping',
      label: 'Prototyping & Demonstrators',
      description:
        'Hardware prototypes, system demonstrators, and scaled antenna systems (1m–2m aperture).',
      priority: 'medium',
      details: [
        'Hardware prototypes for multi-beam antenna systems',
        'System demonstrators for defense evaluation',
        'Scaled antenna systems (1m–2m aperture)',
      ],
    },
    {
      id: 'manufacturing',
      label: 'Early-Stage Manufacturing',
      description:
        'Pilot production in the United States including mechanical integration, electronics module assembly, and system-level validation.',
      priority: 'medium',
      details: [
        'US-based pilot production facility',
        'Mechanical integration of antenna systems',
        'Electronics module integration',
        'System-level assembly and validation',
      ],
    },
  ],

  geography: {
    primary_geography: 'U.S. Federal',
    secondary_geographies: ['Poland', 'EU', 'NATO'],
    state: 'MO',
    city: 'Chesterfield',
    eu_presence: {
      planned: true,
      country: 'Poland',
      region: 'Lubelskie / Podkarpackie',
    },
    eligible_jurisdictions: [
      'U.S. Federal',
      'Poland',
      'European Union',
      'NATO DIANA',
      'Lubelskie (regional)',
      'Podkarpackie (regional)',
    ],
  },

  eligibility_checklist: {
    jurisdiction: 'United States & Poland (planned)',
    items: [
      'Valid FEIN number (US entity)',
      'Current SAM.gov registration',
      'Valid UEI number',
      'Not on Federal Excluded Parties List',
      'Polish KRS registration (planned EU entity)',
      'EU PIC number (Participant Identification Code)',
    ],
    groups: [
      {
        label: 'United States',
        items: [
          'Valid FEIN number',
          'Current SAM.gov registration',
          'Valid UEI number',
          'Not on Federal Excluded Parties List',
        ],
      },
      {
        label: 'Poland (in progress \u2014 expected April 2026)',
        items: [
          'KRS registration (National Court Register) \u2014 in progress, expected by end of April 2026',
          'EU PIC number (Participant Identification Code)',
          'EU linked enterprise classification (Reg. 651/2014 Annex I) \u2014 SME status confirmed',
        ],
        unlock_note: 'Polish entity registration unlocks FENG SMART (up to 70% funding for medium enterprises in Podkarpackie), EDF consortium eligibility, INNOGLOBO, and regional programmes in Lubelskie/Podkarpackie. Client confirms SME classification under EU linked enterprise rules (Reg. 651/2014 Annex I).',
      },
    ],
  },

  programs: [...GENERATED_PROGRAMS],

  timeline: [
    {
      label: 'Q2 2026',
      programs: [
        {
          program_id: 'darpa-dso-baa-HR001125S0013',
          action: 'apply',
          notes: 'HIGHEST PRIORITY (Composite: 4.70): Submit executive summary via BAAT by Jun 2. Target thrust areas (2) Sensing + (3) Math/Computation. Use \u2264$2M accelerated award path for 30-day decision.',
        },
        {
          program_id: 'darpa-tto-baa-HR001125S0011',
          action: 'apply',
          notes: 'URGENT (Composite: 4.35): ES deadline was Apr 17 \u2014 confirm with HR001125S0011@darpa.mil if still accepted. Full Proposal deadline Jun 22. Focus: Design/Build/Buy or Disruptive Innovation.',
        },
        {
          program_id: 'nswc-crane-baa-N0016424SNB35',
          action: 'apply',
          notes: 'HIGH PRIORITY (Composite: 4.30): Email T2 BAA Coordinator to identify Technical POCs for TC1 (EW), TC5 (Advanced Electronics), TC6 (Sensors). Submit 1-5 page white paper. Rolling \u2014 no deadline pressure.',
        },
        {
          program_id: '98fe75ba-a0af-4473-99af-ab762454a28b',
          action: 'prepare',
          notes: 'HIGH PRIORITY (Composite: 4.35): Complete KRS registration. Pursue PARP implementation call (Apr\u2013May 2026) for existing R&D results. Prepare for Oct\u2013Nov 2026 PARP R&D call targeting KIS 9. Engage Polish consultant.',
        },
        {
          program_id: 'edf-2026-da-sens-cew',
          action: 'prepare',
          notes: 'Polish entity registration in progress; identify EU consortium partners for EDF CEW (strongest EU fit, Composite: 3.70). Submission Sept 29.',
        },
      ],
    },
    {
      label: 'Q3 2026',
      programs: [
        {
          program_id: 'darpa-i2o-baa-HR001126S0001',
          action: 'apply',
          notes: 'HIGH PRIORITY (Composite: 4.35): Submit 5-page abstract via BAAT targeting Transformative AI thrust. Abstracts due Nov 1 but submit early for review buffer.',
        },
        {
          program_id: 'devcom-arl-baa-W911NF-23-S-0001',
          action: 'prepare',
          notes: 'Browse ARL BAA topics website to identify sensor/EW/network TPOCs. Make preliminary inquiry. Consider STIR award (\u2264$60K, 9mo) as rapid entry point. (Composite: 4.00)',
        },
        {
          program_id: 'edf-2026-da-sens-cew',
          action: 'apply',
          notes: 'Submit EDF CEW proposal by Sept 29 (EUR 24M, strongest EU fit)',
        },
      ],
    },
    {
      label: 'Q4 2026',
      programs: [
        {
          program_id: 'darpa-i2o-baa-HR001126S0001',
          action: 'apply',
          notes: 'If invited from Q3 abstract: submit full I2O proposal by Nov 30. Include red-lined Model OT Agreement.',
        },
        {
          program_id: 'devcom-arl-baa-W911NF-23-S-0001',
          action: 'apply',
          notes: 'Submit ARL whitepaper (\u22645 pages) to identified TPOC. Or submit STIR proposal (\u2264$60K) for rapid concept validation.',
        },
        {
          program_id: 'nswc-crane-baa-N0016424SNB35',
          action: 'apply',
          notes: 'Continue rolling submissions to NSWC Crane \u2014 submit additional white papers for new TC areas identified in Q2-Q3.',
        },
      ],
    },
    {
      label: 'Q1 2027',
      programs: [
        {
          program_id: '98fe75ba-a0af-4473-99af-ab762454a28b',
          action: 'apply',
          notes: 'Submit PARP FENG SMART R&D application (Oct\u2013Nov 2026 call). Polish entity should be fully operational. Build on implementation track experience if Apr\u2013May call was pursued.',
        },
        {
          program_id: 'edf-2026-da-sens-cew',
          action: 'monitor',
          notes: 'EDF CEW evaluation results expected. If successful, begin consortium coordination for project kick-off.',
        },
      ],
    },
  ],

  priority_overrides: [
    { program_id: 'darpa-dso-baa-HR001125S0013', realistic_funding: '$1M–$2M', funding_intensity: '100%' },
    { program_id: '18c39ae6-28bd-4dcc-b9c5-c9194fe079e6', realistic_funding: '$500K–$3M', funding_intensity: '100%' },
    { program_id: 'darpa-i2o-baa-HR001126S0001', realistic_funding: '$1M–$3M', funding_intensity: '100%' },
    { program_id: 'nswc-crane-baa-N0016424SNB35', realistic_funding: '$500K–$2M', funding_intensity: '100%' },
    { program_id: 'devcom-arl-baa-W911NF-23-S-0001', realistic_funding: '$60K–$500K', funding_intensity: '100%' },
    { program_id: 'edf-2026-da-sens-cew', realistic_funding: '€1M–€3M (consortium share)', funding_intensity: '65–100%' },
    { program_id: 'edf-2026-ra-sens-msdt', realistic_funding: '€500K–€2M (consortium share)', funding_intensity: '100%' },
    { program_id: '98fe75ba-a0af-4473-99af-ab762454a28b', realistic_funding: 'PLN 5M–15M (~€1.2M–€3.5M)', funding_intensity: '50–70%' },
    { program_id: 'edf-2026-ls-da-sme-nt', realistic_funding: '€500K–€2M (consortium share)', funding_intensity: '65–100%' },
    { program_id: '1fda28e3-be84-4f93-a6c5-a89c8d506bf9', realistic_funding: '€100K–€300K', funding_intensity: '100%' },
    { program_id: 'darpa-tto-baa-HR001125S0011', realistic_funding: '$1M–$3M', funding_intensity: '100%' },
    { program_id: '43b9ac46-d30e-4d38-ab7e-8474495ff923', realistic_funding: '$10M+ (if qualified)', funding_intensity: 'Varies' },
  ],

  primary_count: 2,

  display: {
    show_objective: false,
    show_entry_path: false,
  },

  executive_summary: {
    realistic_funding_oneliner: 'Realistic accessible funding: $2M–$8M over 12–18 months across U.S. and EU programs',
    strategic_statement: 'The strongest near-term path runs through U.S. federal BAAs (DARPA, NSWC Crane) offering 100% funding with 3\u20139 month award cycles. Polish entity registration in April unlocks a parallel EU track \u2014 FENG SMART and EDF \u2014 adding up to 70% co-funded R&D and consortium opportunities through 2027.',
    funding_disclaimer: 'Funding ranges reflect realistic accessible amounts for Tetrasoft based on typical award sizes and applicable intensity rates \u2014 not total programme budgets.',
    funding_scenarios: [
      {
        label: 'Conservative',
        targetable_programs: 3,
        funding_range: '$1.5M–$3M',
        co_funding_burden: 'Minimal (U.S. federal programs cover 100%)',
        time_to_award: '3–9 months',
      },
      {
        label: 'Base',
        targetable_programs: 5,
        funding_range: '$3M–$8M',
        co_funding_burden: '10–35% own contribution on EU programs',
        time_to_award: '6–15 months',
      },
      {
        label: 'Upside',
        targetable_programs: 8,
        funding_range: '$8M–$15M',
        co_funding_burden: '15–35% on EU; 50% cost share on some OTs',
        time_to_award: '9–24 months',
      },
    ],
    cost_match: [
      {
        label: 'Core R&D',
        programs: ['DSO BAA', 'STO BAA', 'I2O BAA', 'ARL BAA'],
        fundable_costs: [
          'Research personnel and AI/ML engineers',
          'Algorithm development and simulation',
          'Computational resources (cloud, HPC)',
          'Subcontractors and indirect costs',
        ],
      },
      {
        label: 'Testing & Validation',
        programs: ['NSWC Crane', 'EDF CEW', 'EDF MSDT', 'EDF SME-NT'],
        fundable_costs: [
          'Test equipment and lab infrastructure',
          'RF validation and system integration',
          'Personnel for test campaigns',
          'Travel for program reviews',
        ],
      },
      {
        label: 'Manufacturing & Pilot Production',
        programs: ['FENG SMART', 'DARPA TTO', 'Defense Production Act Title III'],
        fundable_costs: [
          'Prototype hardware fabrication',
          'Pilot production line setup',
          'Manufacturing process development',
          'System-level assembly and validation',
        ],
      },
    ],
    action_plan: {
      now: [
        { program: 'DARPA DSO BAA', action: 'Submit executive summary via BAAT by June 2 \u2014 use \u2264$2M accelerated path' },
        { program: 'FENG SMART (SME)', action: 'Complete KRS registration; pursue Apr\u2013May implementation call for existing R&D; prepare for Oct\u2013Nov R&D call' },
      ],
      next: [
        { program: 'DARPA I2O BAA', action: 'Draft 5-page abstract for Transformative AI thrust \u2014 submit by Q3 2026' },
        { program: 'NSWC Crane BAA', action: 'Email T2 BAA Coordinator to identify EW/Electronics TPOCs; submit white paper' },
        { program: 'DARPA STO BAA', action: 'Draft 2-page abstract focused on ISAC or cognitive EW capabilities; submit to STO' },
        { program: 'ARL BAA', action: 'Browse topics, contact TPOCs; consider STIR ($60K) as rapid entry point' },
        { program: 'EDF CEW / MSDT', action: 'Identify EU consortium partners; begin ownership guarantee process \u2014 Sept 29 deadline' },
      ],
      later: [],
    },
    poland_note: [
      'FENG SMART is Poland\u2019s flagship R&D grant for SMEs \u2014 50\u201370% funding rate for medium enterprises (70% for industrial research, 50% for development work, per regional aid map in Podkarpackie). Client confirms medium enterprise classification.',
      'Two PARP SME call tracks in 2026: Implementation of existing R&D results (Apr\u2013May 2026, call docs TBD) and new R&D projects (Oct\u2013Nov 2026, PLN 700M budget expected). NCBR consortium R&D call also available Apr 9\u2013Jun 12.',
      'KRS registration (expected April 2026) unlocks FENG SMART, EDF consortium eligibility, and regional programmes in Lubelskie/Podkarpackie.',
      'The 2026 edition removed the previous modular structure \u2014 PARP now runs separate calls for R&D and implementation. Supporting tasks (internationalization, IP, competencies) are included as project tasks.',
    ],
  },

  proposal: {
    consultant: {
      name: 'Ray Crick',
      title: 'Managing Director',
      company: 'Bonum Funding',
      email: 'ray@bonumfunding.com',
    },
    engagement_scope:
      'Comprehensive review of non-dilutive funding programs across U.S. Federal, Polish, and EU sources for advanced RF/antenna systems — covering core R&D, testing infrastructure, prototyping, and early-stage manufacturing.',
    time_horizon: 'Next four quarters (Q2 2026 – Q1 2027)',
    created_at: '2026-04-20',
    version: '2.0',
  },
  deck_recipe: {
    slides: [
        "cover",
        "introduction",
        "summary",
        "executive-action",
        "funding-reality",
        "priority-programs",
        "not-prioritizing",
        "requirements",
        "program-table",
        "layer-overview",
        "detail-rd",
        "detail-testing",
        "detail-prototyping",
        "detail-manufacturing",
        "deadline-calendar",
        "decision-memo",
        "timeline",
        "deferred-priority"
    ],
  },

};
